import * as React from 'react';
import {RefObject, useEffect, useRef} from 'react';

import {
    useResidenceExplorerStore,
    selectCurrentClickedArea,
    selectObjectDataFromCurrentSelection,
    selectSelectedObjectId,
    selectSetup,
} from "features/residenceExplorer/store/useResidenceExplorerStore";
import {ObjectStatus} from "features/search/filters/enums";
import {IS_IN_EDIT_MODE} from "features/ui/constants";
import { LinkStyle } from 'features/ui/enums';
import {useMetaStore, selectIsInEditMode} from 'features/meta/store/useMetaStore';
import {gtmEventsPriceListExplorerClick} from "features/services/googleTagManager/events";

import './_selectedObjectPopup.scss';

import {A} from "@atoms/LinksAndButtons/A";
import {Icon} from "@atoms/Media/Icon";
import {ResidenceExplorerObjectCard} from "./_residenceExplorerObjectCard";

interface Props {
    parentView: RefObject<HTMLDivElement | null>;
}

export const SelectedObjectPopup: React.FunctionComponent<Props> = ({parentView}) => {
    const object = useResidenceExplorerStore(selectObjectDataFromCurrentSelection);
    const currentSelectedObject = useResidenceExplorerStore(selectSelectedObjectId);
    const selectedClickArea = useResidenceExplorerStore(selectCurrentClickedArea);
    const setup = useResidenceExplorerStore(selectSetup);
    const setSelectedObjectId = useResidenceExplorerStore(state => state.setSelectedObjectId);
    const isInEditMode = useMetaStore(selectIsInEditMode);
    const [left, setLeft] = React.useState<number>(-10000);
    const [top, setTop] = React.useState<number>(-10000);

    const infoBoxRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if(!infoBoxRef.current) return;

        if(!currentSelectedObject) {
            setLeft(-10000);
            setTop(-10000);
            return;
        }

        changePosition();
    }, [currentSelectedObject, infoBoxRef]);

    const changePosition = () => {
        if (!parentView.current || !selectedClickArea) return;

        const element = document.getElementsByClassName('residence-explorer-view__path--active').item(0);

        if (!element || !infoBoxRef.current) return;

        // Margins and bounds for positioning
        const infoBoxEdgeMargin = 70;
        const infoBoxAreaMargin = 10; // 10px margin from the element
        const infoBoxBounds = infoBoxRef.current.getBoundingClientRect(); // Dynamically get the info box's size
        const wrapperBounds = parentView.current.getBoundingClientRect();
        const targetBounds = element.getBoundingClientRect();

        // Initial position outside and to the right of the target element
        let left = targetBounds.right - wrapperBounds.left + infoBoxAreaMargin;

        // Check if the info box overflows the right side of the wrapper
        if (left + infoBoxBounds.width > wrapperBounds.width - infoBoxEdgeMargin) {
            // If it overflows, flip the box to the left of the target
            left = targetBounds.left - wrapperBounds.left - infoBoxBounds.width - infoBoxAreaMargin;
            if (left < infoBoxEdgeMargin) {
                left = infoBoxEdgeMargin; // Ensure it doesn’t go off the left edge
            }
        }

        // Initial position outside and below the target element
        let top = targetBounds.bottom - wrapperBounds.top + infoBoxAreaMargin;

        // Check if the info box overflows the bottom side of the wrapper
        if (top + infoBoxBounds.height > wrapperBounds.height - infoBoxEdgeMargin) {
            // If it overflows, flip the box above the target
            top = targetBounds.top - wrapperBounds.top - infoBoxBounds.height - infoBoxAreaMargin;
            if (top < infoBoxEdgeMargin) {
                top = infoBoxEdgeMargin; // Ensure it doesn’t go off the top edge
            }
        }

        // Set the calculated left and top values
        setLeft(left);
        setTop(top);
    };

    if (!setup) return null;

    // Rendera inget när ingen bostad är vald. På desktop göms popupen annars genom att
    // positioneras off-screen (left/top -10000), men på mobil är .selected-residence
    // position: static så left/top ignoreras - då blev den alltid renderade stängknappen
    // synlig utan vald bostad. Se AB#75024.
    if (!currentSelectedObject) return null;

    const closePopup = () => {
        setLeft(-10000);
        setTop(-10000);
        setSelectedObjectId('');
    };

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        const url = e.currentTarget?.href && new URL(e.currentTarget.href);

        if(!object) return;

        if (url) {
            gtmEventsPriceListExplorerClick(
                setup.stageName,
                object.stageName || '',
                object.apartmentNumber || '',
            )();
        }
    };

    //if(left === 0 && top === 0) return null;

    const objectUrl = isInEditMode && object ? `${object.url}?editMode=${IS_IN_EDIT_MODE}` : object ? object.url : '';

    return (
        <div className="selected-residence" style={{ left: left, top: top }} ref={infoBoxRef}>
            <div className="selected-residence__close" onClick={closePopup}>
                <Icon name="x" />
            </div>
            {object &&
                <>
                    {object.objectStatus !== ObjectStatus.SHOWING_APARTMENT ?
                        <A 
                            href={objectUrl} 
                            onClick={handleLinkClick}
                            route
                            variant={LinkStyle.UNSTYLED}
                            className="selected-residence__content"
                            title={object.name} 
                        > 
                            <ResidenceExplorerObjectCard object={object} />
                        </A> :
                        <div className="selected-residence__content no-click">
                            <ResidenceExplorerObjectCard object={object} />
                        </div>
                    }
                </>
            }
        </div>
    );
};
